import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  IconButton,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import AppConfigForm from '../../../components/Forms/AppConfigForm';
import { appConfigService } from '../../../services/apiService';
import { showToast } from '../../../utils/toast';

const AppConfigTab = ({ onAddRef }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await appConfigService.getAll();
        const configs = res?.data || res || [];
        setConfigs(Array.isArray(configs) ? configs : []);
      } catch (err) {
        console.error('Error loading configurations:', err);
        setError(err?.response?.data?.error || err?.message || 'Failed to load configurations');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refresh = async () => {
    try {
      const res = await appConfigService.getAll();
      const configs = res?.data || res || [];
      setConfigs(Array.isArray(configs) ? configs : []);
    } catch (err) {
      console.error('Error refreshing configurations:', err);
      showToast.error(err?.response?.data?.error || err?.message || 'Failed to refresh configurations');
    }
  };

  const handleAdd = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  useEffect(() => {
    if (onAddRef) {
      onAddRef(handleAdd);
    }
  }, [handleAdd, onAddRef]);

  const handleEdit = (row) => {
    setEditing(row);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleModalSave = async (formData) => {
    try {
      setModalLoading(true);
      if (editing) {
        await appConfigService.update(editing.id, formData);
        showToast.success('Configuration updated successfully');
        if (editing.config_key === 'SCHOOL_NAME' || editing.config_key === 'THEME') {
          setTimeout(() => {
            window.location.reload();
          }, 500);
          return;
        }
      } else {
        await appConfigService.create(formData);
        showToast.success('Configuration created successfully');
        if (formData.config_key === 'SCHOOL_NAME' || formData.config_key === 'THEME') {
          setTimeout(() => {
            window.location.reload();
          }, 500);
          return;
        }
      }
      await refresh();
      handleModalClose();
    } catch (err) {
      const errorMsg = err?.response?.data?.error || err?.message || 'Action failed';
      showToast.error(errorMsg);
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1.5, md: 2 }, 
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
    }}>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : configs.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No configurations found
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: { xs: 1.5, sm: 1.75 },
          }}
        >
          {configs.map((config) => (
            <Paper
              key={config.id}
              variant="outlined"
              sx={{
                p: { xs: 2, sm: 2.4 },
                borderRadius: 2,
                borderColor: 'divider',
                position: 'relative',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 2,
                  borderColor: 'primary.main',
                },
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      fontFamily: 'monospace',
                      mb: 1,
                      color: 'primary.main',
                    }}
                  >
                    {config.config_key}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Value:</strong> {config.config_value || '—'}
                  </Typography>
                  {config.description && (
                    <Typography variant="body2" color="text.secondary">
                      {config.description}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(config)}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.light',
                        color: 'primary.dark',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      <AppConfigForm
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSave}
        initialData={editing}
        loading={modalLoading}
        title={editing ? 'Edit Configuration' : 'Add Configuration'}
      />
    </Box>
  );
};

export default AppConfigTab;
